import React, { useState, useEffect } from 'react';
import { Search, MapPin, Clock, Plus, Upload, Trash2 } from 'lucide-react';
import { getApiUrl } from '@/config';
import { AssignmentModal } from '../AssignmentModal';
import { useDraggable } from '@dnd-kit/core';


interface Assignment {
    id: string;
    clientName: string;
    address: string;
    city?: string; // Correct typing for optional fields from import
    duration: number;
    type: string;
    notes?: string;
    status: 'todo' | 'in_progress' | 'done' | 'blocked';
}

interface DraggableAssignmentCardProps {
    assignment: Assignment;
    onDelete: (id: string) => void;
}

const DraggableAssignmentCard: React.FC<DraggableAssignmentCardProps> = ({ assignment, onDelete }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `backlog-${assignment.id}`,
        data: {
            type: 'assignment',
            assignment: assignment
        }
    });

    const style = {
        opacity: isDragging ? 0.3 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...listeners}
            {...attributes}
            className="bg-glass border border-glass-border rounded-lg p-3 hover:bg-glass-border/20 transition-colors cursor-move group touch-none relative shadow-sm"
        >
            <div className="flex justify-between items-start mb-2">
                <h3 className="font-medium text-main text-sm truncate pr-6">{assignment.clientName}</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30 whitespace-nowrap">
                    {assignment.type || 'Maintenance'}
                </span>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted mb-1">
                <MapPin size={12} />
                <span className="truncate">{assignment.address} {assignment.city ? `- ${assignment.city}` : ''}</span>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted">
                <Clock size={12} />
                <span>{assignment.duration} min</span>

                {assignment.notes && (
                    <span className="ml-auto text-yellow-500/80" title="Notes disponibles">
                        Note
                    </span>
                )}
            </div>

            {/* Delete Action (Top right absolute) */}
            <button
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1 bg-red-500/20 hover:bg-red-500 text-red-400 hover:text-white rounded transition-all"
                onClick={(e) => {
                    e.stopPropagation(); // Prevent drag start
                    if (confirm("Supprimer cette intervention ?")) onDelete(assignment.id);
                }}
            >
                <Trash2 size={14} />
            </button>
        </div>
    );
};

interface AssignmentBacklogProps {
    version?: number;
}

export const AssignmentBacklog: React.FC<AssignmentBacklogProps> = ({ version = 0 }) => {
    const [assignments, setAssignments] = useState<Assignment[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    // Filters
    const [filterType, setFilterType] = useState<string>('All');
    const [filterCity, setFilterCity] = useState<string>('All');

    useEffect(() => {
        fetchBacklog();

        const handleRefresh = () => fetchBacklog();
        window.addEventListener('refresh_backlog', handleRefresh);
        return () => window.removeEventListener('refresh_backlog', handleRefresh);
    }, [version]);

    const fetchBacklog = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl('/api/assignments?technicianId=null'), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (res.ok) {
                const data = await res.json();
                setAssignments(data);
            }
        } catch (e) {
            console.error('Failed to fetch backlog', e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            setIsLoading(true);
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl('/api/assignments/import'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData,
            });

            if (res.ok) {
                const data = await res.json();
                // Success - show debug info
                console.log('Import debug:', data.debug);
                console.log('Types imported:', data.types);

                // Alert avec les infos
                alert(`Import réussi!\n${data.count} interventions importées.\n\nTypes détectés: ${data.types.join(', ')}\n\nVoir la console pour plus de détails.`);

                await fetchBacklog();
            } else {
                console.error("Import failed");
                alert("Erreur lors de l'import");
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            alert("Erreur de connexion");
        } finally {
            setIsLoading(false);
            event.target.value = '';
        }
    };

    const handleDelete = async (id: string) => {
        try {
            const token = localStorage.getItem('token');
            await fetch(getApiUrl(`/api/assignments/${id}`), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            setAssignments(prev => prev.filter(a => a.id !== id));
        } catch (e) {
            console.error('Delete failed', e);
        }
    };

    // Derived Filters Options
    const types = ['All', ...Array.from(new Set(assignments.map(a => a.type || 'Autre'))).sort()];
    // Heuristic for city: often in address or explicit field. 
    // Assuming 'city' might be populated or we parse from address if easy, but user asked for "ville que je demande" so let's use the explicit city field if available or just generic filter.
    const cities = ['All', ...Array.from(new Set(assignments.map(a => a.city).filter(Boolean) as string[])).sort()];

    const filteredAssignments = assignments.filter(a => {
        const matchesSearch =
            a.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            a.address.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesType = filterType === 'All' || (a.type || 'Autre') === filterType;
        const matchesCity = filterCity === 'All' || a.city === filterCity;

        return matchesSearch && matchesType && matchesCity;
    });

    return (
        <div className="w-80 border border-glass-border bg-glass flex flex-col h-full rounded-2xl overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-glass-border space-y-3">
                <div className="flex justify-between items-center">
                    <h2 className="font-bold text-main">À Planifier <span className="text-muted text-sm ml-2">({filteredAssignments.length})</span></h2>
                    <div className="flex items-center gap-1">
                        <label className="p-1 hover:bg-glass-border/20 rounded text-muted hover:text-main transition-colors cursor-pointer" title="Importer CSV">
                            <input type="file" accept=".csv" className="hidden" onChange={handleFileUpload} />
                            <Upload size={20} />
                        </label>
                        <button onClick={() => setIsCreateModalOpen(true)} className="p-1 hover:bg-glass-border/20 rounded text-primary transition-colors">
                            <Plus size={20} />
                        </button>
                    </div>
                </div>

                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={16} />
                    <input
                        type="text"
                        placeholder="Rechercher..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-glass border border-glass-border rounded-lg py-2 pl-9 pr-3 text-sm text-main placeholder-muted focus:outline-none focus:border-primary/50"
                    />
                </div>

                {/* Filters */}
                {(cities.length > 1 || types.length > 1) && (
                    <div className="flex gap-2">
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="bg-glass border border-glass-border rounded text-xs text-main py-1 px-2 flex-1 focus:outline-none"
                        >
                            {types.map(t => <option key={t} value={t} className="bg-app text-main">{t}</option>)}
                        </select>

                        <select
                            value={filterCity}
                            onChange={(e) => setFilterCity(e.target.value)}
                            className="bg-glass border border-glass-border rounded text-xs text-main py-1 px-2 flex-1 focus:outline-none"
                        >
                            <option value="All" className="bg-app text-main">Toutes villes</option>
                            {cities.map(c => <option key={c} value={c} className="bg-app text-main">{c}</option>)}
                        </select>
                    </div>
                )}

                {/* Bulk Actions */}
                {filteredAssignments.length > 0 && (
                    <div className="flex justify-end">
                        <button
                            onClick={async () => {
                                if (confirm(`Supprimer ces ${filteredAssignments.length} interventions visible(s) ?`)) {
                                    setIsLoading(true);
                                    // Parallel deletes
                                    const token = localStorage.getItem('token');
                                    await Promise.all(filteredAssignments.map(a => fetch(getApiUrl(`/api/assignments/${a.id}`), {
                                        method: 'DELETE',
                                        headers: { 'Authorization': `Bearer ${token}` }
                                    })));
                                    await fetchBacklog();
                                    setIsLoading(false);
                                }
                            }}
                            className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 px-2 py-1 rounded hover:bg-red-500/10 transition-colors"
                        >
                            <Trash2 size={12} />
                            Tout supprimer
                        </button>
                    </div>
                )}
            </div>

            {/* List - FORCE HIDE SCROLLBAR */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3 hide-scrollbar-force">
                {isLoading ? (
                    <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                ) : filteredAssignments.length === 0 ? (
                    <div className="text-center py-10 text-muted text-sm">
                        Aucune intervention à planifier.
                    </div>
                ) : (
                    filteredAssignments.map(assignment => (
                        <DraggableAssignmentCard
                            key={assignment.id}
                            assignment={assignment}
                            onDelete={handleDelete}
                        />
                    ))
                )}
            </div>

            {isCreateModalOpen && (
                <AssignmentModal
                    isOpen={isCreateModalOpen}
                    onClose={() => setIsCreateModalOpen(false)}
                    date={new Date()} // Default to today, user can change or it will simply be ignored if we modify AssignmentModal to support "No Date"
                    availableTechnicians={[]} // Empty tech list implies "Backlog" / Unassigned
                    technician={undefined}
                    onSave={async (_techId: string, _updates: any) => {
                        // This callback is usually for updating a tech.
                        // But AssignmentModal handles the creation logic internally or we can intercept here.
                        // Actually, looking at AssignmentModal, it handles creation via API if we want it to?
                        // Wait, AssignmentModal current implementation (Step 2784) tries to update a specific technician.
                        // We need to leverage its UI but catch the "Create" action for a backlog item (no tech).

                        // However, AssignmentModal requires `availableTechnicians` to assign someone.
                        // The user wants the "SAME MODEL" as clicking on the planning.
                        // Clicking on planning always requires picking a tech?
                        // No, if I click "in the void" implies assigning to someone?
                        // Actually, 'Backlog' implies NO technician.

                        // If I reuse AssignmentModal, I need to allow "No Technician Selected".
                        // But AssignmentModal is built around `selectedTechIds`.

                        // Let's refactor AssignmentModal slightly to support "Backlog Mode" (no tech required) 
                        // OR we just intercept the save there.

                        // Actually, the user says "same modal as when I click in the planning".
                        // In the planning, you click a cell (Date + Tech) -> specific tech.
                        // OR you click empty space?

                        // To satisfy "Same UI", I should use AssignmentModal.
                        // I will pass a dummy "Backlog" technician or similar to satisfy types, 
                        // OR better: I will simply trigger a refresh here after the modal closes.

                        await fetchBacklog();
                        setIsCreateModalOpen(false);
                    }}
                />
            )}
        </div>
    );
};
